/**
 * What a run or a job came to, in words (specs/component-pipeline.md AC-7).
 *
 * Five kinds and no more, because the reader acts on five: it is still going,
 * it worked, it broke, somebody or something stopped it, or GitHub reported
 * something this portal does not know. The last is the one that matters: a
 * conclusion GitHub introduces tomorrow must never read as a success, so the
 * fallback carries the raw word rather than borrowing a known one.
 */

export type OutcomeKind =
  'running' | 'success' | 'failure' | 'neutral' | 'unknown';

export type Outcome = { kind: OutcomeKind; label: string };

const FAILURE: Record<string, string> = {
  failure: 'Failed',
  timed_out: 'Timed out',
  startup_failure: 'Startup failure',
};

// Finished without a verdict on the build — stopped, not broken (DX-344).
const NEUTRAL: Record<string, string> = {
  cancelled: 'Cancelled',
  skipped: 'Skipped',
  neutral: 'Neutral',
  stale: 'Stale',
};

export function outcomeOf(item: {
  status: string | null;
  conclusion: string | null;
}): Outcome {
  if (item.status && item.status !== 'completed') {
    // `waiting` is a person or a timer holding the run — an environment's
    // required reviewer, typically — and reads differently from a run that
    // only waits for a runner.
    const label: Record<string, string> = {
      in_progress: 'Running',
      waiting: 'Waiting',
    };
    return { kind: 'running', label: label[item.status] ?? 'Queued' };
  }
  const conclusion = item.conclusion;
  if (conclusion === 'success') return { kind: 'success', label: 'Success' };
  if (conclusion && FAILURE[conclusion]) {
    return { kind: 'failure', label: FAILURE[conclusion] };
  }
  if (conclusion && NEUTRAL[conclusion]) {
    return { kind: 'neutral', label: NEUTRAL[conclusion] };
  }
  return { kind: 'unknown', label: conclusion ?? 'Unknown' };
}
